import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { useConfigId } from '../../contexts/ConfigContext'
import { getGraphSettings, updateSetting, getTransitions, updateTransitions, getVariableDefinitions } from '../../api/graph_settings'
import { NODES, MEDIA_SELECTOR_SETTING_KEYS } from '../../config/graphEntities'
import ConditionBuilder, { L3_KEYS, L2_VAR_KEYS } from '../../components/ConditionBuilder'
import MediaSelectorDescription from './MediaSelectorDescription'
import SettingsVariablesContent from './SettingsVariables'
import { PageSection, SettingsFormWithPanel } from '../../components/ui'
import '../../styles/pages/page-common.css'
import '../../styles/pages/settings.css'

function normalizeGraphTransitions(raw) {
  const tr = { ...(raw || {}) }
  const adv = tr.advance_script_on_free_send
  if (adv === 'true' || adv === '1') tr.advance_script_on_free_send = true
  if (adv === 'false' || adv === '0') tr.advance_script_on_free_send = false
  return tr
}

export default function SettingsNodeDetail() {
  const { t } = useI18n()
  const configId = useConfigId()
  const { nodeKey } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const node = NODES.find((n) => n.key === nodeKey)
  const [transitions, setTransitions] = useState({})
  const [l4Definitions, setL4Definitions] = useState([])

  // Локальное состояние для редактирования (сохраняется по кнопке внизу)
  const [localSettings, setLocalSettings] = useState({})
  const [localTransitions, setLocalTransitions] = useState({})
  const [variablesDirty, setVariablesDirty] = useState(false)
  const variablesRef = React.useRef()

  useEffect(() => {
    if (!nodeKey) return
    setLoading(true)
    setError(null)

    const needsTransitions =
      configId != null && (nodeKey === 'core' || nodeKey === 'media_selector')

    if (needsTransitions) {
      Promise.all([
        getGraphSettings(configId),
        getTransitions(configId),
        getVariableDefinitions(configId),
      ])
        .then(([graphRes, trRes, vdRes]) => {
          setData(graphRes)
          const tr = normalizeGraphTransitions(trRes?.transitions)
          setTransitions(tr)
          setLocalTransitions(tr)
          const defs = vdRes?.definitions || []
          setL4Definitions(Array.isArray(defs) ? defs : [])
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false))
      return
    }

    getGraphSettings(configId)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [nodeKey, configId])

  useEffect(() => {
    if (data?.settings) setLocalSettings(data.settings)
  }, [data?.settings])

  const settings = data?.settings || {}
  const effectiveSettings = nodeKey === 'media_selector'
    ? { ...settings, ...localSettings }
    : { ...settings, ...localSettings }

  function isDirty() {
    if (nodeKey === 'media_selector') {
      const mediaChanged = MEDIA_SELECTOR_SETTING_KEYS.some(
        (k) => String(localSettings[k] ?? '') !== String(settings[k] ?? '')
      )
      const transChanged = JSON.stringify(localTransitions) !== JSON.stringify(transitions)
      return mediaChanged || transChanged
    }
    if (node?.settingKeys) {
      const settingsChanged = node.settingKeys.some(
        (k) => String(localSettings[k] ?? '') !== String(settings[k] ?? '')
      )
      const transChanged =
        nodeKey === 'core'
          ? JSON.stringify(localTransitions) !== JSON.stringify(transitions)
          : false
      return settingsChanged || variablesDirty || transChanged
    }
    return variablesDirty
  }

  async function handleSaveAll() {
    setSaving(true)
    try {
      if (nodeKey === 'media_selector') {
        for (const k of MEDIA_SELECTOR_SETTING_KEYS) {
          const v = localSettings[k]
          if (v !== undefined && String(v) !== String(settings[k] ?? '')) {
            await updateSetting(k, v, configId)
          }
        }
        await updateTransitions(localTransitions, configId)
        setTransitions(localTransitions)
      } else if (node?.settingKeys) {
        for (const k of node.settingKeys) {
          const v = localSettings[k]
          if (v !== undefined && String(v) !== String(settings[k] ?? '')) {
            await updateSetting(k, v, configId)
          }
        }
        if (nodeKey === 'core') {
          await updateTransitions(localTransitions, configId)
          setTransitions(localTransitions)
        }
      }

      // Сохраняем переменные L4, если они изменились
      if (variablesDirty && variablesRef.current?.save) {
        await variablesRef.current.save()
      }

      setData((prev) => ({
        ...prev,
        settings: { ...prev?.settings, ...localSettings },
      }))
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const dirty = isDirty()
  const hasPanel = (node?.hasSettings || nodeKey === 'media_selector')

  if (!node) {
    return (
      <div className="settings-section">
        <p className="settings-error">{t('common.error')}: {t('settings.nodeNotFound')}</p>
        <Link to={NODES[0]?.key || 'in'}>← {t('settings.nav.nodes')}</Link>
      </div>
    )
  }

  if (loading) return <p className="settings-loading">{t('common.loading')}</p>
  if (error) return <p className="settings-error">{error}</p>

  const content = (
    <PageSection
        title={node.labelKey ? t(node.labelKey) : node.label}
        contentClassName="settings-section-block"
      >
        {nodeKey === 'media_selector' ? (
          <MediaSelectorDescription
            settings={effectiveSettings}
            onChange={(key, value) => setLocalSettings((s) => ({ ...s, [key]: value }))}
          />
        ) : node.descriptionKey ? (
          <div className="settings-node-desc-block">
            <p className="settings-node-desc">{t(node.descriptionKey)}</p>
          </div>
        ) : null}
        {node.hasSettings && nodeKey !== 'media_selector' ? (
          <div className="settings-grid">
            {node.settingKeys.map((key) => (
              <SettingRow
                key={key}
                keyName={key}
                value={effectiveSettings[key]}
                onChange={(v) => setLocalSettings((s) => ({ ...s, [key]: v }))}
                t={t}
              />
            ))}
          </div>
        ) : null}
        {nodeKey === 'core' && (
          <div className="settings-node-desc-block settings-node-subheading settings-core-transitions-intro">
            <p className="settings-node-desc">{t('settings.core.graphTransitionsHeading')}</p>
          </div>
        )}
        {nodeKey === 'core' && (
          <div className="settings-edges-list settings-node-transitions settings-core-postbuy-block">
            <TransitionRow
              label={t('settings.transition.postbuyBlock')}
              hint={t('settings.transition.postbuyBlockHint')}
              condition={localTransitions.postbuy_block}
              onChange={(cond) => setLocalTransitions((tr) => {
                const next = { ...tr }
                if (cond == null || (typeof cond === 'object' && Object.keys(cond).length === 0)) {
                  delete next.postbuy_block
                } else {
                  next.postbuy_block = cond
                }
                return next
              })}
              l4Definitions={l4Definitions}
              graphTransitions={localTransitions}
              showEvalPreview
              showMonetizationGatePreview
            />
          </div>
        )}
        {nodeKey === 'core' && (
          <div className="settings-core-variables">
            <SettingsVariablesContent
              ref={variablesRef}
              onDirtyChange={setVariablesDirty}
              hidePanel={true}
              onBeforeSave={async () => {
                const keys = node?.settingKeys || []
                for (const k of keys) {
                  const v = localSettings[k]
                  if (v !== undefined && String(v) !== String(settings[k] ?? '')) {
                    await updateSetting(k, v, configId)
                  }
                }
                setData((prev) => ({ ...prev, settings: { ...prev?.settings, ...localSettings } }))
              }}
            />
          </div>
        )}
        {nodeKey === 'media_selector' && (
          <div className="settings-edges-list settings-node-transitions">
            <div className="settings-node-desc-block settings-node-subheading">
              <p className="settings-node-desc">{t('settings.mediaSelector.routingHeading')}</p>
            </div>
            <TransitionRow
              label={t('settings.transition.salesBranch')}
              hint={t('settings.transition.salesBranchHint')}
              condition={localTransitions.run_monetization}
              onChange={(cond) => setLocalTransitions((t) => {
                const next = { ...t }
                if (cond == null || (typeof cond === 'object' && Object.keys(cond).length === 0)) {
                  delete next.run_monetization
                } else {
                  next.run_monetization = cond
                }
                return next
              })}
              l4Definitions={l4Definitions}
              graphTransitions={localTransitions}
              showEvalPreview
              showMonetizationGatePreview
            />
            <TransitionRow
              label={t('settings.transition.emitChatMedia')}
              hint={t('settings.transition.emitChatMediaHint')}
              condition={localTransitions.emit_chat_media}
              onChange={(cond) => setLocalTransitions((t) => {
                const next = { ...t }
                if (cond == null || (typeof cond === 'object' && Object.keys(cond).length === 0)) {
                  delete next.emit_chat_media
                } else {
                  next.emit_chat_media = cond
                }
                return next
              })}
              l4Definitions={l4Definitions}
              showEvalPreview
            />
            <TransitionRow
              label={t('settings.transition.advanceScriptOnFreeSend')}
              hint={t('settings.transition.advanceScriptOnFreeSendHint')}
              condition={localTransitions.advance_script_on_free_send}
              allowPlainBoolean
              onChange={(cond) => setLocalTransitions((tr) => {
                const next = { ...tr }
                if (cond === null || cond === undefined) {
                  delete next.advance_script_on_free_send
                } else if (
                  typeof cond === 'object'
                  && cond !== null
                  && Object.keys(cond).length === 0
                ) {
                  delete next.advance_script_on_free_send
                } else {
                  next.advance_script_on_free_send = cond
                }
                return next
              })}
              l4Definitions={l4Definitions}
              showEvalPreview
            />
          </div>
        )}
        {!node.hasSettings && nodeKey !== 'core' && (
          <div className="settings-no-settings">
            <p className="settings-hint">{t('settings.noSettings')}</p>
            {node.noSettingsHintKey && (
              <p className="settings-node-hint">{t(node.noSettingsHintKey)}</p>
            )}
          </div>
        )}
      </PageSection>
  )

  return hasPanel ? (
    <SettingsFormWithPanel dirty={dirty} saving={saving} onSave={handleSaveAll}>
      {content}
    </SettingsFormWithPanel>
  ) : (
    <div>{content}</div>
  )
}

function TransitionRow({
  label,
  hint,
  condition,
  onChange,
  l4Definitions = [],
  showEvalPreview = false,
  allowPlainBoolean = false,
  graphTransitions = null,
  showMonetizationGatePreview = false,
}) {
  const l4Keys = l4Definitions.map((d) => d.key).filter(Boolean)
  const paramKeys = [...new Set([...L2_VAR_KEYS, ...L3_KEYS, ...l4Keys])]

  return (
    <div className="settings-edge-row">
      <div className="settings-edge-header">
        <h3 className="settings-edge-label">{label}</h3>
        <span className="settings-edge-hint">{hint}</span>
      </div>
      <div className="settings-edge-condition">
        <ConditionBuilder
          condition={condition}
          onChange={onChange}
          paramKeys={paramKeys}
          l4Keys={l4Keys}
          l4Definitions={l4Definitions}
          confirmRemove
          showEvalPreview={showEvalPreview}
          allowPlainBoolean={allowPlainBoolean}
          graphTransitions={graphTransitions}
          showMonetizationGatePreview={showMonetizationGatePreview}
        />
      </div>
    </div>
  )
}

function SettingRow({ keyName, value, onChange, t }) {
  const desc = t(`settings.desc.${keyName}`)
  const hasDesc = desc && desc !== `settings.desc.${keyName}`

  return (
    <div className="setting-row">
      <div className="setting-label-wrap">
        <label className="setting-key">{t(`settings.key.${keyName}`) || keyName}</label>
        {hasDesc && <span className="setting-desc">{desc}</span>}
      </div>
      <input
        type="text"
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        className="setting-value"
      />
    </div>
  )
}
